import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core';
import RateCardIcon from '@material-ui/icons/AttachMoneySharp';
import {
  EditableCard2,
  Edit,
  EditContent,
  EditTitle,
  ReadOnly,
  ReadOnlyContent
} from '~/modules/common/components/EditableCard';
import {
  useAvailableCostOverrideDimensions,
  useProjectCostOverrideDimensionHandlers,
  useProjectCostOverrideErrors,
  usePutProjectCostRates,
  mapCostOverrideRates
} from '../hooks';
import { ProjectCostOverridesActionComponent } from './ProjectCostOverridesActionComponent';
import { ProjectCostOverridesDialogContent } from './ProjectCostOverridesDialogContent';
import { ProjectCostOverridesReadOnlyContent } from './ProjectCostOverridesReadOnlyContent';

const avatar = <RateCardIcon />;

const useCardStyles = makeStyles(theme => ({
  card: {
    marginTop: theme.spacing(2)
  }
}));

const useEditContentStyles = makeStyles({
  noVerticalPadding: {
    paddingTop: 0,
    paddingBottom: 0
  }
});

const useDialogActionStyles = makeStyles(theme => ({
  dialogActions: {
    display: 'block',
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3)
  }
}));

const getEditMaxWidth = (selectedDimensions, projectRates) => {
  const dimensionsCount = selectedDimensions?.length || 0;
  const hasEffectiveDate = projectRates.some(r => !r.isInitialRate);

  if (dimensionsCount > 2) return 'xl';
  if (dimensionsCount > 1) return 'lg';
  if (dimensionsCount === 0 && hasEffectiveDate) return 'md';
  if (dimensionsCount === 0) return 'sm';

  return 'md';
};

const getReadOnlyMaxWidth = (selectedDimensions, projectRates) => {
  const dimensionsCount = selectedDimensions?.length || 0;
  const hasEffectiveDate = projectRates.some(r => !r.isInitialRate);

  if (dimensionsCount > 2) return 'lg';
  if (dimensionsCount > 1) return 'md';
  if (dimensionsCount === 0 && hasEffectiveDate) return 'sm';
  if (dimensionsCount === 0) return 'sm';

  return 'sm';
};

const getMaxWidth = (selectedDimensions, projectRates, viewSummary) =>
  viewSummary
    ? getReadOnlyMaxWidth(selectedDimensions, projectRates)
    : getEditMaxWidth(selectedDimensions, projectRates);

export const ProjectCostOverridesEditable = ({
  editable = false,
  projectSlug,
  projectUri,
  projectCurrency,
  projectRates = [],
  projectRateCount = 0,
  projectDimensions = [],
  hasMore = false,
  loadingMore = false,
  loadMore,
  edit = false,
  viewSummary = false,
  onViewSummaryClick,
  onCancelCallback
}) => {
  const { formatMessage } = useIntl();
  const cardClasses = useCardStyles();
  const editContentClasses = useEditContentStyles();
  const dialogActionClasses = useDialogActionStyles();

  const transformedRates = useMemo(
    () => mapCostOverrideRates(projectRates, projectDimensions),
    [projectRates, projectDimensions]
  );

  const editor = useProjectCostOverrideDimensionHandlers({
    projectCurrency,
    initialSelectedDimensions: projectDimensions,
    initialProjectRates: transformedRates
  });

  const { availableDimensions } = useAvailableCostOverrideDimensions({
    selectedDimensions: editor.selectedDimensions
  });

  const { putProjectCostRates, isSaving } = usePutProjectCostRates();

  const hasDimensionsWithoutRates =
    editor.selectedDimensions.length > 0 &&
    editor.projectRates.length === 0 &&
    editor.persistedRates.length === 0;

  const {
    selectedDimensions: editorSelectedDimensions,
    projectRates: editorProjectRates,
    commit: editorCommit
  } = editor;

  const { errors, hasErrors } = useProjectCostOverrideErrors({
    selectedDimensions: editorSelectedDimensions,
    projectRates: editorProjectRates,
    formatMessage
  });

  const onSave = useCallback(async () => {
    if (isSaving || hasErrors) return;
    await putProjectCostRates({
      projectId: projectUri,
      selectedDimensions: editorSelectedDimensions,
      projectRates: editorProjectRates
    });
    editorCommit();
  }, [
    editorCommit,
    editorProjectRates,
    editorSelectedDimensions,
    hasErrors,
    isSaving,
    projectUri,
    putProjectCostRates
  ]);

  const maxWidth = getMaxWidth(
    editor.selectedDimensions,
    editor.projectRates,
    viewSummary
  );
  const isEditable = viewSummary ? false : editable;

  const handleCancel = onCancelCallback || editor.reset;
  const titleId = viewSummary
    ? 'projectCostOverrides.summary.cardTitle'
    : 'projectCostOverrides.cardTitle';

  const actionComponentCustomProps = useMemo(
    () => ({
      readOnly: viewSummary,
      selectedDimensions: editor.selectedDimensions,
      onAddNewGroup: editor.formOnChange.onAddNewGroup,
      isLocked: loadingMore || hasMore
    }),
    [
      viewSummary,
      editor.selectedDimensions,
      editor.formOnChange.onAddNewGroup,
      loadingMore,
      hasMore
    ]
  );

  return (
    <EditableCard2
      editable={isEditable}
      edit={edit}
      classes={cardClasses}
      editContentClassName={editContentClasses.noVerticalPadding}
      dialogActionClasses={dialogActionClasses}
      maxWidth={maxWidth}
      ariaLabelKey="projectCostOverrides.cardTitle"
      dataQeId="ProjectCostOverridesCard"
    >
      <ReadOnly
        expandable={false}
        avatar={avatar}
        title={formatMessage({ id: 'projectCostOverrides.cardTitle' })}
      >
        <ReadOnlyContent>
          <ProjectCostOverridesReadOnlyContent
            projectRateCount={projectRateCount}
            projectDimensions={projectDimensions}
            onViewSummary={onViewSummaryClick}
          />
        </ReadOnlyContent>
      </ReadOnly>
      <Edit
        onSave={onSave}
        onCancel={handleCancel}
        saveable={
          !isSaving &&
          editor.isDirty &&
          !hasDimensionsWithoutRates &&
          !hasErrors
        }
        ActionComponent={ProjectCostOverridesActionComponent}
        actionComponentCustomProps={actionComponentCustomProps}
      >
        <EditTitle>{formatMessage({ id: titleId })}</EditTitle>
        <EditContent>
          <ProjectCostOverridesDialogContent
            projectSlug={projectSlug}
            projectCurrency={projectCurrency}
            editor={editor}
            availableDimensions={availableDimensions}
            errors={errors}
            hasMore={hasMore}
            loadingMore={loadingMore}
            loadMore={loadMore}
            readOnly={viewSummary}
          />
        </EditContent>
      </Edit>
    </EditableCard2>
  );
};

ProjectCostOverridesEditable.propTypes = {
  editable: PropTypes.bool,
  projectSlug: PropTypes.string,
  projectUri: PropTypes.string,
  projectCurrency: PropTypes.object,
  projectRates: PropTypes.array,
  projectRateCount: PropTypes.number,
  projectDimensions: PropTypes.array,
  hasMore: PropTypes.bool,
  loadingMore: PropTypes.bool,
  loadMore: PropTypes.func,
  edit: PropTypes.bool,
  viewSummary: PropTypes.bool,
  onViewSummaryClick: PropTypes.func,
  onCancelCallback: PropTypes.func
};

export default ProjectCostOverridesEditable;
